export interface User {
  userId: string;
  cognitoUserId: string;
  email: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  registrationNumber?: string;
  specialization?: string;
  profilePhoto?: string;
  organizationId?: string;
  organizationName?: string;
  /** Superadmin viewing a hospital read-only; `role` is then 'admin' for navigation */
  supportMode?: boolean;
  realRole?: UserRole;
}

export enum UserRole {
  DOCTOR = 'doctor',
  PATIENT = 'patient',
  RECEPTIONIST = 'receptionist',
  LAB_TECHNICIAN = 'lab_technician',
  RADIOLOGIST = 'radiologist',
  PATHOLOGIST = 'pathologist',
  ADMIN = 'admin',
  SUPERADMIN = 'superadmin',
}

export interface Patient {
  id: string;
  abhaId?: string;
  abhaAddress?: string;
  userId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'Male' | 'Female' | 'Other';
  weight?: number;
  bloodGroup?: string;
  phoneNumber: string;
  email?: string;
  address?: Address;
  allergies?: string[];
  chronicConditions?: string[];
  profilePhoto?: string;
  fullName: string;
  age: number;
  ageDisplay?: string;
  ageInDays?: number;
  ageInWeeks?: number;
  // Pediatric-specific fields
  birthWeight?: number; // in kg
  birthLength?: number; // in cm
  birthHeadCircumference?: number; // in cm
  gestationalAge?: number; // in weeks
  deliveryType?: 'Normal' | 'Cesarean' | 'Assisted';
  motherName?: string;
  fatherName?: string;
  
  // Gynecology-specific fields
  lastMenstrualPeriod?: string; // LMP date
  menstrualCycleLength?: number; // days (typical 21-35)
  menstrualFlowDuration?: number; // days (typical 3-7)
  menstrualFlowType?: 'Light' | 'Normal' | 'Heavy';
  gravida?: number; // Total number of pregnancies (G)
  para?: number; // Number of live births (P)
  abortions?: number; // Miscarriages/terminations (A)
  menarcheAge?: number; // Age at first menstruation
  menopauseDate?: string; // If applicable
  gynecologicalConditions?: string[]; // PCOS, Endometriosis, Fibroids, etc.
  contraceptionMethod?: string; // Pills, IUD, Condoms, etc.
  lastPapSmear?: string;
  lastPapSmearResult?: string; // Normal, Abnormal, ASCUS, etc.
  lastMammogram?: string;
  lastMammogramResult?: string;
  isPregnant?: boolean;
  expectedDeliveryDate?: string; // EDD
  isBreastfeeding?: boolean;
  reproductiveHealthDataConsent?: boolean;
  sexualHealthDataConsent?: boolean;
  
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

export interface Prescription {
  _id: string;
  prescriptionNumber: string;
  patientId: string;
  patientName?: string;
  doctorId: string;
  doctorName?: string;
  visitDate: string;
  diagnosis: string;
  chiefComplaint?: string;
  symptoms: string[];
  vitalSigns?: VitalSigns;
  gynecologyVitalSigns?: GynecologyVitalSigns;
  medications: Medication[];
  notes?: string;
  labTestsOrdered: LabTest[];
  followUpDate?: string;
  digitalSignature?: DigitalSignature;
  pdfUrl?: string;
  status: 'Draft' | 'Signed' | 'Dispensed' | 'Cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface Medication {
  drugName: string;
  genericName?: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions?: string;
  route?: 'Oral' | 'Topical' | 'Injection' | 'Inhalation';
}

export interface LabTest {
  testName: string;
  urgency?: 'Routine' | 'Urgent' | 'STAT';
}

export interface VitalSigns {
  bloodPressure?: string;
  temperature?: number;
  pulse?: number;
  weight?: number;
  height?: number;
  oxygenSaturation?: number;
  respiratoryRate?: number;
}

export interface GynecologyVitalSigns {
  fundalHeight?: number; // cm
  fetalHeartRate?: number; // bpm
  contractionFrequency?: string;
  cervicalDilation?: number; // cm (0-10)
  effacement?: number; // percentage (0-100)
  presentation?: string; // Cephalic, Breech, Transverse
  edema?: string; // None, Mild, Moderate, Severe
}

export interface DigitalSignature {
  signed: boolean;
  signatureData?: string;
  certificateSerial?: string;
  signedAt?: string;
  signedBy?: string;
  method?: 'simple_rsa' | 'aadhaar_esign';
  algorithm?: string;
}

export interface LabReport {
  _id: string;
  reportNumber: string;
  patientId: string;
  prescriptionId?: string;
  testType: string;
  testDate: string;
  results: TestResult[];
  pdfReportUrl?: string;
  status: 'Pending' | 'InProgress' | 'Completed' | 'Reviewed';
  createdAt: string;
  updatedAt: string;
}

export interface TestResult {
  parameter: string;
  value: string;
  unit?: string;
  normalRange?: string;
  flag?: 'Normal' | 'High' | 'Low' | 'Critical';
}

export interface DicomStudy {
  _id: string;
  studyInstanceUID: string;
  patientId: string;
  modality?: string;
  studyDescription?: string;
  thumbnailUrls: string[];
  storageStatus: 'Active' | 'Archived' | 'Restoring' | 'Restored';
  createdAt: string;
}

export interface Appointment {
  id: string;
  patientId: string;
  patientName?: string;
  doctorId: string;
  doctorName?: string;
  appointmentDate: string;
  appointmentTime: string;
  duration?: number;
  status: 'scheduled' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
  type?: 'consultation' | 'follow-up' | 'emergency' | 'checkup';
  reason?: string;
  reasonForVisit?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
  };
}

export interface AbhaProfile {
  abhaNumber: string;
  abhaAddress: string;
  name: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  mobile: string;
  email?: string;
  address: Address;
  photo?: string;
}

// Organization Types
export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  contactEmail: string;
  contactPhone: string;
  address: Address;
  isActive: boolean;
  subscription: OrganizationSubscription;
  features: OrganizationFeatures;
  hierarchy: OrganizationHierarchy;
  createdAt: string;
  updatedAt: string;
}

export enum OrganizationType {
  HOSPITAL = 'Hospital',
  CLINIC = 'Clinic',
  LABORATORY = 'Laboratory',
  DIAGNOSTIC_CENTER = 'Diagnostic Center',
  PHARMACY = 'Pharmacy',
}

export interface OrganizationSubscription {
  tier: SubscriptionTier;
  startDate: string;
  expiryDate: string;
  isActive: boolean;
}

export enum SubscriptionTier {
  BASIC = 'Basic',
  PRO = 'Pro',
  ENTERPRISE = 'Enterprise',
}

export interface OrganizationFeatures {
  maxUsers: number;
  maxPatients: number;
  maxStorageGB: number;
  enableTelemedicine: boolean;
  enableLabIntegration: boolean;
  enableDicom: boolean;
  enableAI: boolean;
  enableDataSharing: boolean;
}

export interface OrganizationHierarchy {
  parentOrganizationId?: string;
  childOrganizationIds: string[];
}

export interface OrganizationSettings {
  id?: string;
  organizationId: string;
  branding?: OrganizationBranding;
  enabledFeatures?: string[];
  features?: OrganizationFeatureSettings; // For UI convenience
  retention?: DataRetentionSettings;
  prescription?: PrescriptionSettings;
  prescriptionSettings?: PrescriptionSettings; // Alias
  billing?: { consultationFee: number; followUpFee: number; gstin?: string; upiId?: string; upiPayeeName?: string };
  billingSettings?: { consultationFee: number; followUpFee: number; gstin?: string; upiId?: string; upiPayeeName?: string };
  configuration?: string | Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrganizationBranding {
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  customCss?: string;
}

export interface OrganizationFeatureSettings {
  enableTelemedicine?: boolean;
  enableLabIntegration?: boolean;
  enableDicom?: boolean;
  enablePrescriptions?: boolean;
  enableAppointments?: boolean;
  enableDataSharing?: boolean;
}

export interface DataRetentionSettings {
  prescriptionsDays?: number;
  appointmentsDays?: number;
  labReportsDays?: number;
  dicomStudiesDays?: number;
  auditLogsDays?: number;
  // Legacy fields for backwards compatibility
  patientRecordsDays?: number;
  appointmentRecordsDays?: number;
}

export interface PrescriptionSettings {
  clinicName?: string;
  clinicAddress?: string;
  clinicPhone?: string;
  clinicEmail?: string;
  licenseNumber?: string;
  registrationNumber?: string;
  taxNumber?: string;
  footerText?: string;
  showLogo?: boolean;
  headerText?: string;
}

export interface OrganizationMetrics {
  organizationId: string;
  month: string;
  counters: {
    patients: number;
    appointments: number;
    prescriptions: number;
    lab_reports: number;
    dicom_studies: number;
    api_calls: number;
  };
  storageUsedMB: number;
  activeUsers: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationMember {
  id?: string;
  organizationId: string;
  userId: string;
  role: string;
  isPrimary?: boolean;
  isPrimaryOrganization?: boolean; // Legacy field
  isActive?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  joinedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
}

export enum OrganizationMemberRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  READ_ONLY = 'readonly',
  DOCTOR = 'doctor',
  RECEPTIONIST = 'receptionist',
  LAB_TECHNICIAN = 'lab_technician',
  PATHOLOGIST = 'pathologist',
  RADIOLOGIST = 'radiologist',
}

export interface OrganizationApiKey {
  id: string;
  organizationId: string;
  name: string;
  keyName?: string; // Alias for name
  keyPrefix: string;
  keyHash?: string;
  permissions?: string[];
  usageCount: number;
  lastUsedAt?: string;
  expiresAt?: string;
  expiryDate?: string; // Alias for expiresAt
  isActive: boolean;
  createdBy?: string;
  createdAt?: string;
  plainKey?: string; // Only returned on creation
}

export interface CrossOrgShareRequest {
  id: string;
  fromOrganizationId: string;
  toOrganizationId: string;
  patientId: string;
  requestedDataTypes: string[];
  purpose: string;
  status: ShareRequestStatus;
  requestedBy: string;
  approvedBy?: string;
  deniedBy?: string;
  expiryDate: string;
  createdAt: string;
  updatedAt: string;
  fromOrganization?: Organization;
  toOrganization?: Organization;
}

export enum ShareRequestStatus {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  DENIED = 'Denied',
  REVOKED = 'Revoked',
  EXPIRED = 'Expired',
}

export interface PatientTransfer {
  id: string;
  patientId: string;
  fromOrganizationId: string;
  toOrganizationId: string;
  reason: string;
  status: TransferStatus;
  fromOrgApprovedBy?: string;
  fromOrgApprovedAt?: string;
  toOrgApprovedBy?: string;
  toOrgApprovedAt?: string;
  completedAt?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  deniedBy?: string;
  deniedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  patient?: Patient;
  fromOrganization?: Organization;
  toOrganization?: Organization;
}

export enum TransferStatus {
  PENDING_FROM_ORG_APPROVAL = 'PendingFromOrgApproval',
  PENDING_TO_ORG_APPROVAL = 'PendingToOrgApproval',
  APPROVED_BY_BOTH = 'ApprovedByBoth',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  DENIED = 'Denied',
}

export interface OrganizationTemplate {
  id: string;
  name: string;
  type: OrganizationType;
  isSystem: boolean;
  config: {
    subscriptionTier: SubscriptionTier;
    features: OrganizationFeatures;
    settings: Partial<OrganizationSettings>;
  };
  createdAt: string;
  updatedAt: string;
}

// Receptionist-specific types
export interface CheckIn {
  id: string;
  appointmentId: string;
  patientId: string;
  checkInTime: string;
  status: CheckInStatus;
  waitingAreaAssignment?: string;
  notes?: string;
  checkedInBy: string;
  createdAt: string;
  updatedAt: string;
}

export enum CheckInStatus {
  CHECKED_IN = 'checked_in',
  WAITING = 'waiting',
  WITH_DOCTOR = 'with_doctor',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface QueueItem {
  id: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  position: number;
  estimatedWaitTime: number; // in minutes
  priority: QueuePriority;
  status: QueueStatus;
  checkInTime: string;
  patientName: string;
  doctorName: string;
}

export enum QueuePriority {
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum QueueStatus {
  WAITING = 'waiting',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export interface AppointmentWithDetails extends Appointment {
  patientDetails?: Patient;
  doctorDetails?: User;
  checkInStatus?: CheckInStatus;
}

export interface PatientSearchResult {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string;
  email?: string;
  dateOfBirth: string;
  age: number;
  abhaId?: string;
}

export interface ReceptionistStats {
  todayAppointments: number;
  checkedIn: number;
  waiting: number;
  completed: number;
  cancelled: number;
  averageWaitTime: number;
}

// =============================================================================
// Vaccination Types
// =============================================================================

export enum VaccinationStatus {
  SCHEDULED = 'Scheduled',
  OVERDUE = 'Overdue',
  ADMINISTERED = 'Administered',
  MISSED = 'Missed',
  CANCELLED = 'Cancelled',
}

export enum ReminderStatus {
  PENDING = 'Pending',
  SENT = 'Sent',
  FAILED = 'Failed',
  SKIPPED = 'Skipped',
}

export interface VaccineDose {
  doseNumber: number;
  ageMonths: number;
  intervalDays?: number;
  description?: string;
}

export interface Vaccine {
  id: string;
  name: string;
  shortName: string;
  description?: string;
  manufacturer?: string;
  category: string;
  targetDisease?: string;
  totalDoses?: number;
  isPediatric: boolean;
  doseSchedule: VaccineDose[];
  ageRangeMonthsMin?: number;
  ageRangeMonthsMax?: number;
  sideEffects?: string[];
  contraindications?: string[];
  notes?: string;
  isOptional?: boolean;
  isActive: boolean;
  organizationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VaccinationRecord {
  id: string;
  patientId: string;
  patientName?: string;
  vaccineId: string;
  vaccineName: string;
  doseNumber: number;
  scheduledDate: string;
  administeredDate?: string;
  administeredBy?: string;
  administeredByName?: string;
  batchNumber?: string;
  siteOfAdministration?: string;
  status: VaccinationStatus;
  notes?: string;
  parentGuardianConsent?: boolean;
  remindersSent: number;
  lastReminderSentAt?: string;
  nextReminderDate?: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface VaccinationScheduleTemplate {
  id: string;
  name: string;
  description?: string;
  vaccines: TemplateVaccine[];
  reminderDaysBeforeVaccination: number[];
  isActive: boolean;
  isDefault: boolean;
  organizationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateVaccine {
  vaccineId: string;
  vaccineName: string;
  doses: TemplateVaccineDose[];
}

export interface TemplateVaccineDose {
  doseNumber: number;
  ageWeeks: number;
  notes?: string;
}

export interface ScheduleVaccinationRequest {
  patientId: string;
  vaccineId: string;
  doseNumber: number;
  scheduledDate: string;
  notes?: string;
}

export interface ScheduleFromTemplateRequest {
  patientId: string;
  templateId: string;
  startDate?: string;
}

export interface AdministerVaccinationRequest {
  administeredDate: string;
  batchNumber?: string;
  siteOfAdministration?: string;
  notes?: string;
  parentGuardianConsent?: boolean;
}

export interface RescheduleVaccinationRequest {
  newScheduledDate: string;
  reason?: string;
}

export interface VaccinationStats {
  totalScheduled: number;
  totalAdministered: number;
  totalOverdue: number;
  totalMissed: number;
  totalCancelled: number;
  upcomingThisWeek: number;
  upcomingThisMonth: number;
  administeredThisMonth: number;
  vaccineCoverage: VaccineCoverage[];
}

export interface VaccineCoverage {
  vaccineName: string;
  totalPatients: number;
  fullyVaccinated: number;
  partiallyVaccinated: number;
  notStarted: number;
  coveragePercentage: number;
}
